/**
 * Shared API contract types for the Sphere Viewer.
 *
 * The `type`/`interface` exports are erased at build time, so importing them
 * with `import type { ... }` costs nothing at runtime on either side.
 *
 * The runtime **type guards** at the bottom let each side verify that an
 * `unknown` payload matches the expected shape before trusting it — the api
 * validates incoming request bodies, and the frontend can validate the
 * responses it reads back. They are tiny and dependency-free.
 *
 * Consumers inline this source rather than importing a build: the api via
 * esbuild (see `api/esbuild.mjs`, which bundles workspace packages), and the
 * frontend via Vite (the saved-views client in
 * `src/features/saved-views/saved-views-client.ts`). There is no compiled
 * output and no runtime node_modules dependency.
 */

/**
 * A camera payload as stored in a saved view.
 *
 * Structurally identical to the viewer's `CameraParameters` but expressed with
 * plain, dependency-free types (number tuples instead of `three` vectors) so it
 * can live in this shared package. Orthographic cameras carry `vsize`,
 * perspective cameras carry `fov` — exactly one is present.
 */
export type SavedViewCamera = {
  /** The near plane. */
  near: number;
  /** The far plane. */
  far: number;
  /** The camera position `[x, y, z]`. */
  pos: [number, number, number];
  /** The point the camera looks at `[x, y, z]`. */
  target: [number, number, number];
} & ({ vsize: number } | { fov: number });

/**
 * An oriented-bounding-box (clipping) payload as stored in a saved view.
 *
 * Structurally identical to the viewer's `OrientedBoundingBox`, using plain
 * number tuples.
 */
export type SavedViewClipping = {
  /** The center of the clipping box `[x, y, z]`. */
  position: [number, number, number];
  /** The clipping box rotation as a quaternion `[x, y, z, w]`. */
  rotation: [number, number, number, number];
  /** The size of the clipping box `[x, y, z]`. */
  size: [number, number, number];
};

/**
 * A project-filtering payload as stored in a saved view.
 *
 * Structurally identical to the viewer's `ProjectFilteringSlice` (`GUID` and
 * `ISOTimeString` are both `string`).
 */
export type SavedViewFiltering = {
  /** The search text, case insensitive. */
  text: string;
  /** The selected tag ids. */
  tags?: string[];
  /** The selected date range (ISO-8601 strings). */
  dateRange: {
    /** Inclusive start of the range. */
    start?: string;
    /** Inclusive end of the range. */
    end?: string;
  };
};

/**
 * The client-owned payload stored in a saved view — and the single source of
 * truth for what the api is allowed to persist.
 *
 * It is **concrete and dependency-free** so it can live in this shared package:
 * enum-like fields (`mode`/`tool`/`scene`/`rightScene`) are plain `string`s
 * (the viewer will narrow them to its own enums), and geometry uses number
 * tuples instead of `three` types.
 *
 * The specific values of the `string` enum fields are deliberately not checked
 * here — the viewer validates them against the current project when it resolves
 * a view, so an unknown value degrades gracefully instead of blocking
 * persistence.
 */
export type SavedViewData = {
  /** The id of the active element. Required for a valid view unless `lookAtId` is set. */
  id?: string;
  /** An element to look at, distinct from the active element. */
  lookAtId?: string;
  /** The mode to open (a `ModeNames` value in the viewer). */
  mode?: string;
  /** A tool to activate when the view opens (a `ToolName` value in the viewer). */
  tool?: string;
  /** The id of the active cad, if any. */
  cadId?: string;
  /** The camera configuration of the (main) view. */
  camera?: SavedViewCamera;
  /** The active clipping box. */
  clipping?: SavedViewClipping;
  /** The active scene filter of the (main) view (a `SceneFilter` value in the viewer). */
  scene?: string;
  /** The id of the comparison element in split mode. */
  rightId?: string;
  /** The camera configuration of the comparison view in split mode. */
  rightCam?: SavedViewCamera;
  /** The scene filter of the comparison view in split mode (a `SceneFilter` value in the viewer). */
  rightScene?: string;
  /** Whether the (main) view prefers intensity panorama images over colored ones. */
  intensity?: boolean;
  /** Whether the comparison view in split mode prefers intensity panorama images. */
  rightIntensity?: boolean;
  /** The active project filtering state (search text, tags, date range). */
  filtering?: SavedViewFiltering;
  /** Whether to sync cameras rotations */
  syncRot?: string;
  /** Whether to sync cameras on the selected waypoint*/
  syncWaypoint?: string;
};

/**
 * A saved view as returned by the api.
 *
 * The {@link SavedView.id} is surfaced to users via share links such as
 * `viewer.holobuilder.com/share/<id>`.
 */
export interface SavedView {
  /** Short, URL-safe share id. */
  id: string;
  /** The id of the project this saved view belongs to. */
  projectId: string;
  /** The client-owned payload. */
  data: SavedViewData;
  /** ISO-8601 timestamp of creation. */
  createdAt: string;
  /** ISO-8601 timestamp of the last update. Equal to `createdAt` while the api exposes no update route. */
  updatedAt: string;
}

/** Request body for creating a saved view. */
export type SavedViewBody = {
  /** The id of the project this saved view belongs to. */
  projectId: string;
  /** The client-owned payload. */
  data: SavedViewData;
  /**
   * Optional lifetime of the saved view, in seconds from creation.
   *
   * Omitting it — the default — stores a permanent view. When present it maps
   * directly onto Cosmos' native per-item `ttl`, so expiry is enforced by the
   * database rather than by this API, and the timing is best-effort.
   *
   * Must be an integer between {@link SAVED_VIEW_MIN_TTL_SECONDS} and
   * {@link SAVED_VIEW_MAX_TTL_SECONDS}.
   */
  ttl?: number;
};

/** Response body of a successful `POST /api/savedView`, carrying only the new id. */
export type SavedViewCreated = Pick<SavedView, "id">;

/** Error response body returned by the api on failures. */
export interface ApiError {
  /** Human-readable description of the error. */
  error: string;
}

// ---------------------------------------------------------------------------
// Runtime type guards
//
// Validate `unknown` payloads before relying on their shape. The api uses these
// to validate incoming request bodies; the response guards are here for the
// frontend to validate what it reads back, e.g.
// `if (!isSavedView(body)) throw new Error("…")`.
// ---------------------------------------------------------------------------

/**
 * Upper bounds on the variable-length parts of a saved view.
 *
 * The api accepts saved views from anonymous callers, so every string and array
 * needs a ceiling: without one a single document could grow to Cosmos' 2 MB
 * item limit and the endpoint would double as free, unbounded storage. The
 * values are far above any view the viewer produces — they cap abuse, they are
 * not a UI constraint.
 */
export const SAVED_VIEW_LIMITS = {
  /** Ids (project, element, cad, tag) and enum-like fields (mode/tool/scene). */
  id: 64,
  /** ISO-8601 timestamps in the filtering date range. */
  timestamp: 64,
  /** Free-text project search. */
  text: 256,
  /** Number of tag ids a filter may carry. */
  tags: 100,
} as const;

/**
 * Shortest accepted {@link SavedViewBody.ttl}.
 *
 * A floor keeps a view from expiring before its creator can load their own share
 * link — network latency, a retry, or a slow CI runner can easily eat a handful
 * of seconds. It is not an abuse control.
 */
export const SAVED_VIEW_MIN_TTL_SECONDS = 60;

/**
 * Longest accepted {@link SavedViewBody.ttl}: Cosmos stores a per-item `ttl` as
 * a signed 32-bit number of seconds, so this is the largest value it can hold
 * (~68 years).
 *
 * There is deliberately no shorter *policy* cap — omitting `ttl` already stores
 * the view permanently, so a long but finite lifetime is never the more
 * expensive option. This ceiling exists only so an out-of-range value is
 * rejected here with a `400` instead of failing inside Cosmos on write and
 * surfacing as a `500`.
 */
export const SAVED_VIEW_MAX_TTL_SECONDS = 2_147_483_647;

/**
 * Narrows an unknown value to a plain object (not null, not an array).
 *
 * @param value - The value to test.
 * @returns `true` if the value is a non-null, non-array object.
 */
function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * Rejects objects carrying keys outside a known set, so payloads stay strictly
 * matched to their contract (no silent passthrough of unexpected fields).
 *
 * @param value - The object to test.
 * @param allowedKeys - The complete set of permitted keys.
 * @returns `true` if every own key of `value` is in `allowedKeys`.
 */
function hasOnlyKeys(
  value: Record<string, unknown>,
  allowedKeys: readonly string[],
): boolean {
  return Object.keys(value).every((key) => allowedKeys.includes(key));
}

/**
 * @param value - The value to test.
 * @param maxLength - The longest accepted string.
 * @returns `true` if the value is a string no longer than `maxLength`.
 */
function isBoundedString(value: unknown, maxLength: number): value is string {
  return typeof value === "string" && value.length <= maxLength;
}

/**
 * @param value - The value to test.
 * @param maxLength - The longest accepted string.
 * @returns `true` if the value is undefined or a string within `maxLength`.
 */
function isOptionalBoundedString(value: unknown, maxLength: number): boolean {
  return value === undefined || isBoundedString(value, maxLength);
}

/**
 * Whether a value is a real, finite number.
 *
 * `typeof value === "number"` alone is not enough: JSON has no literal for
 * infinity, but a numeric literal that overflows a double — `1e999` — is
 * *valid* JSON that `JSON.parse` turns into `Infinity`. Such a value would then
 * be re-serialized as `null` on the way into storage, silently corrupting a
 * camera or clipping box instead of being rejected as a bad request. `NaN`
 * cannot come out of `JSON.parse`, but is excluded by the same check for
 * callers that validate in-memory objects.
 *
 * @param value - The value to test.
 * @returns `true` if the value is a number that is neither infinite nor `NaN`.
 */
function isFiniteNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

/**
 * @param value - The value to test.
 * @returns `true` if the value is a boolean.
 */
function isBoolean(value: unknown): value is boolean {
  return typeof value === "boolean";
}

/**
 * @param value - The value to test.
 * @param length - The exact required length.
 * @returns `true` if the value is an array of `length` finite numbers.
 */
function isNumberTuple(value: unknown, length: number): boolean {
  return (
    Array.isArray(value) &&
    value.length === length &&
    value.every(isFiniteNumber)
  );
}

/** Keys of a valid camera payload (orthographic uses `vsize`, perspective `fov`). */
const CAMERA_KEYS = ["near", "far", "pos", "target", "vsize", "fov"] as const;

/**
 * Validates a camera payload as stored in a saved view.
 *
 * This is the single source of truth for camera validation: both the backend
 * and the frontend reach it through {@link isSavedViewData}. It is
 * dependency-free so both sides share one strict implementation.
 *
 * @param value - The value to validate.
 * @returns `true` if the value is a valid orthographic or perspective camera.
 */
export function isSavedViewCamera(value: unknown): value is SavedViewCamera {
  if (!isPlainObject(value) || !hasOnlyKeys(value, CAMERA_KEYS)) {
    return false;
  }
  if (
    !isFiniteNumber(value.near) ||
    !isFiniteNumber(value.far) ||
    !isNumberTuple(value.pos, 3) ||
    !isNumberTuple(value.target, 3)
  ) {
    return false;
  }
  // Exactly one projection parameter must be present, and it must be a number.
  const hasVsize = "vsize" in value;
  const hasFov = "fov" in value;
  if (hasVsize === hasFov) {
    return false;
  }
  return hasVsize ? isFiniteNumber(value.vsize) : isFiniteNumber(value.fov);
}

/** Keys of a valid oriented-bounding-box (clipping) payload. */
const CLIPPING_KEYS = ["position", "rotation", "size"] as const;

/**
 * Validates a clipping box (oriented bounding box) payload.
 *
 * @param value - The value to validate.
 * @returns `true` if the value has a numeric position/size (3) and rotation (4).
 */
export function isSavedViewClipping(
  value: unknown,
): value is SavedViewClipping {
  return (
    isPlainObject(value) &&
    hasOnlyKeys(value, CLIPPING_KEYS) &&
    isNumberTuple(value.position, 3) &&
    isNumberTuple(value.rotation, 4) &&
    isNumberTuple(value.size, 3)
  );
}

/** Keys of a valid project-filtering payload. */
const FILTERING_KEYS = ["text", "tags", "dateRange"] as const;
/** Keys of a valid filtering date-range payload. */
const DATE_RANGE_KEYS = ["start", "end"] as const;

/**
 * Validates a project-filtering payload (search text, tags, date range).
 *
 * @param value - The value to validate.
 * @returns `true` if the value matches the filtering shape.
 */
export function isSavedViewFiltering(
  value: unknown,
): value is SavedViewFiltering {
  if (!isPlainObject(value) || !hasOnlyKeys(value, FILTERING_KEYS)) {
    return false;
  }
  const { text, tags, dateRange } = value;
  if (!isBoundedString(text, SAVED_VIEW_LIMITS.text)) {
    return false;
  }
  if (
    tags !== undefined &&
    !(
      Array.isArray(tags) &&
      tags.length <= SAVED_VIEW_LIMITS.tags &&
      tags.every((tag) => isBoundedString(tag, SAVED_VIEW_LIMITS.id))
    )
  ) {
    return false;
  }
  if (!isPlainObject(dateRange) || !hasOnlyKeys(dateRange, DATE_RANGE_KEYS)) {
    return false;
  }
  return (
    isOptionalBoundedString(dateRange.start, SAVED_VIEW_LIMITS.timestamp) &&
    isOptionalBoundedString(dateRange.end, SAVED_VIEW_LIMITS.timestamp)
  );
}

/**
 * Wraps a validator so an absent (`undefined`) value is accepted, matching an
 * optional field.
 *
 * @param validate - The validator applied when the value is present.
 * @returns a validator that also accepts `undefined`.
 */
function optional(
  validate: (value: unknown) => boolean,
): (value: unknown) => boolean {
  return (value) => value === undefined || validate(value);
}

/**
 * Builds a validator for an optional string field with a length ceiling.
 *
 * @param maxLength - The longest accepted string.
 * @returns a validator accepting `undefined` or a string within `maxLength`.
 */
function optionalText(maxLength: number): (value: unknown) => boolean {
  return (value) => isOptionalBoundedString(value, maxLength);
}

/**
 *
 * @param value The unknown value to validate
 * @returns true iff the input value is either the string "0" or the string "1".
 */
function isZeroOrOne(value: unknown): boolean {
  return value === "0" || value === "1";
}

/**
 * Per-field validators for a {@link SavedViewData} payload.
 *
 * The `satisfies` clause ties this map to {@link SavedViewData}: every field
 * must have a validator and no extra keys are allowed, so adding a field to the
 * type without validating it here is a **compile error** — the type and the
 * runtime guard cannot drift.
 */
const SAVED_VIEW_DATA_VALIDATORS = {
  id: optionalText(SAVED_VIEW_LIMITS.id),
  lookAtId: optionalText(SAVED_VIEW_LIMITS.id),
  mode: optionalText(SAVED_VIEW_LIMITS.id),
  tool: optionalText(SAVED_VIEW_LIMITS.id),
  cadId: optionalText(SAVED_VIEW_LIMITS.id),
  camera: optional(isSavedViewCamera),
  clipping: optional(isSavedViewClipping),
  scene: optionalText(SAVED_VIEW_LIMITS.id),
  rightId: optionalText(SAVED_VIEW_LIMITS.id),
  rightScene: optionalText(SAVED_VIEW_LIMITS.id),
  rightCam: optional(isSavedViewCamera),
  intensity: optional(isBoolean),
  rightIntensity: optional(isBoolean),
  filtering: optional(isSavedViewFiltering),
  syncRot: optional(isZeroOrOne),
  syncWaypoint: optional(isZeroOrOne),
} satisfies Record<keyof SavedViewData, (value: unknown) => boolean>;

/**
 * Validates that a value is a usable {@link SavedViewData} payload.
 *
 * The check is strict: unknown keys are rejected and every known field is
 * type-checked (see {@link SAVED_VIEW_DATA_VALIDATORS}). Enum-valued fields
 * (mode/tool/scene) are only required to be strings; their specific values are
 * validated by the frontend against the current project.
 *
 * @param value - The value to validate.
 * @returns `true` if the value matches the saved-view data shape.
 */
export function isSavedViewData(value: unknown): value is SavedViewData {
  if (!isPlainObject(value)) {
    return false;
  }
  return (
    hasOnlyKeys(value, Object.keys(SAVED_VIEW_DATA_VALIDATORS)) &&
    Object.entries(SAVED_VIEW_DATA_VALIDATORS).every(([key, validate]) =>
      validate(value[key]),
    )
  );
}

/** The complete set of keys a {@link SavedViewBody} request may carry. */
const SAVED_VIEW_BODY_KEYS = ["projectId", "data", "ttl"] as const;

/**
 * Validates an optional {@link SavedViewBody.ttl}.
 *
 * Only the bounds are checked here: they are fixed and environment-independent,
 * so both sides can share them. Whether a *specific* deployment honors a `ttl`
 * is server topology and is checked by the api.
 *
 * @param value - The value to validate.
 * @returns `true` if the value is absent, or an integer number of seconds
 *   between {@link SAVED_VIEW_MIN_TTL_SECONDS} and
 *   {@link SAVED_VIEW_MAX_TTL_SECONDS}.
 */
export function isValidTtl(value: unknown): boolean {
  return (
    value === undefined ||
    (typeof value === "number" &&
      Number.isInteger(value) &&
      value >= SAVED_VIEW_MIN_TTL_SECONDS &&
      value <= SAVED_VIEW_MAX_TTL_SECONDS)
  );
}

/**
 * Validates that a value matches the {@link SavedViewBody} request shape.
 *
 * @param value - The value to validate.
 * @returns `true` if the value matches the {@link SavedViewBody} shape.
 */
export function isSavedViewBody(value: unknown): value is SavedViewBody {
  return (
    isPlainObject(value) &&
    hasOnlyKeys(value, SAVED_VIEW_BODY_KEYS) &&
    isBoundedString(value.projectId, SAVED_VIEW_LIMITS.id) &&
    isSavedViewData(value.data) &&
    isValidTtl(value.ttl)
  );
}

/**
 * Validates that a value matches the {@link SavedView} response shape.
 *
 * @param value - The value to validate.
 * @returns `true` if the value matches the {@link SavedView} shape.
 */
export function isSavedView(value: unknown): value is SavedView {
  return (
    isPlainObject(value) &&
    isBoundedString(value.id, SAVED_VIEW_LIMITS.id) &&
    isBoundedString(value.projectId, SAVED_VIEW_LIMITS.id) &&
    isSavedViewData(value.data) &&
    isBoundedString(value.createdAt, SAVED_VIEW_LIMITS.timestamp) &&
    isBoundedString(value.updatedAt, SAVED_VIEW_LIMITS.timestamp)
  );
}

/**
 * Validates that a value matches the {@link SavedViewCreated} response shape.
 *
 * @param value - The value to validate.
 * @returns `true` if the value matches the {@link SavedViewCreated} shape.
 */
export function isSavedViewCreated(value: unknown): value is SavedViewCreated {
  return (
    isPlainObject(value) && isBoundedString(value.id, SAVED_VIEW_LIMITS.id)
  );
}

/**
 * Validates the {@link ApiError} body returned on failures.
 *
 * @param value - The value to validate.
 * @returns `true` if the value matches the {@link ApiError} shape.
 */
export function isApiError(value: unknown): value is ApiError {
  return isPlainObject(value) && typeof value.error === "string";
}
